var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BroadcastToPeerMessage, ByteStreamWriter, calcStringByteLength, CreateSubModelMessage, DeleteModelOnDisconnectFacet, generateUUID, hashCode, MulticastDelegate, MulticastDelegate1, PeersSelectionVisibilityMessage, PendingMessages, RemoveModelMessage, RequestAvatarModelUIDMessage, ResponseAvatarModelUIDMessage, ROOT_MODEL_UID, UID_BYTES_PER_ELEMENT, UpdateFacetOnModelMessage, } from "flavr-common";
import { PeerAddedMessage, PeerCreatedMessage, PeerRemovedMessage } from "@messages/session/PeerMessages";
import { ChangeLocalUserInfoMessage } from "@messages/session/SessionMessages";
import { HistoryEventType } from "@messages/scripting/ScriptMessages";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { modelPool, modelToAbsolutePath } from "@modules/model";
import { UNDEFINED_CLIENT_ID } from "@modules/users";
import { BufferMessageType } from "../MultiplayerTypes";
import { generateAvatarModelIds } from "./AvatarPeer";
import { NetworkObjectFactory } from "./NetworkObjectFactory";
import { historyEventService } from "./HistoryEventService";
import { PeerMessageHandler } from "./PeerMessageHandler";
import { NetworkMsgPacker } from "./NetworkMsgPacker";
import { ControllerPeer } from "./ControllerPeer";
import { PeerConnection } from "./PeerConnection";
import { usersService } from "../UsersService";
import { serverConnection } from "../server/ServerConnectionService";
import { createChangeRoomBuffer } from "../server/ServerBufferMessagesUtils";
import { ensureLocalPlayerHeadModel, ensureLocalPlayerRootModel } from "@modules/player";
export class PeerManager {
    constructor(connection) {
        this.connection = connection;
        this.peers = new Map();
        this.pendingPeerCreations = new Map();
        this.peerAvatars = new Map();
        this.localControllers = new Map();
        this.peersControllers = new Map();
        this.sendUpdateTime = 1000 / 25;
        this.lastSendUpdate = 0;
        this.sendTimeSyncTime = 1000;
        this.lastSendTimeSync = 0;
        this.syncObjectUids = new Map();
        this.syncObjectsToSend = new Map();
        this.lastSentSyncObjectStates = new Map();
        this.peerSelectionVisible = true;
        /**
         * Mirrors ControllerModule.isShowingVrController.
         * Used by ControllerPeer.initRemote() and updated via ControllerVisibilityMessage
         * so that remote-peer controller meshes respect the same visibility setting as local ones.
         */
        this.isShowingVrController = true;
        this.peerAvatarCreatedDelegate = new MulticastDelegate1();
        this.peerAvatarRemovedDelegate = new MulticastDelegate1();
        this.peerControllerCreatedDelegate = new MulticastDelegate1();
        this.peerControllerRemovedDelegate = new MulticastDelegate1();
        this.lateUpdateDelegate = new MulticastDelegate();
        this.pendingBusMessages = new PendingMessages();
        this.pendingCreateObjects = [];
        this.pendingRemoveObjects = [];
        this.pendingRPCs = [];
        this.currentRoomUID = ROOT_MODEL_UID;
        this.destroyModelsOnDisconnect = new Map();
        this.peerStatsEnabled = false;
        this.handleChangeLocalUserInfoMessage = (msg) => {
            const localUser = usersService.getLocalUser();
            if (localUser.clientId === UNDEFINED_CLIENT_ID) {
                console.warn("[multiplayer] handleChangeLocalUserInfoMessage: No local user set yet, ignoring message");
                return;
            }
            if (msg.params.customUsername) {
                usersService.setCustomUsername(msg.params.customUsername);
            }
            if (msg.params.avatar) {
                localUser.avatar = msg.params.avatar;
            }
            this.broadcastReliable({
                channel: "userinfo",
                payload: {
                    identity: localUser.identity,
                    avatar: localUser.avatar,
                    customUsername: usersService.getCustomUsername(),
                    guest: localUser.guest
                }
            });
        };
        this.onFrame = () => {
            for (const peer of this.peers.values()) {
                peer.update();
            }
            for (const obj of this.syncObjectUids.values()) {
                if (!obj.interpolate)
                    continue;
                const peer = this.getPeer(obj.ownerId);
                if (!peer)
                    continue;
                obj.interpolate(peer.remoteTime);
            }
            if (!this.pendingBusMessages.isEmpty()) {
                this.connection.sendMultiple(this.pendingBusMessages.get());
                this.pendingBusMessages.clear();
            }
            if (this.pendingRemoveObjects.length) {
                const buffer = NetworkMsgPacker.packRemoveObjectsInArrayBuffer(this.pendingRemoveObjects);
                if (buffer) {
                    this.broadcastReliableBuffer(buffer);
                }
                this.pendingRemoveObjects = [];
            }
            if (this.pendingCreateObjects.length) {
                const buffer = NetworkMsgPacker.packCreateObjectsArrayBuffer(this.pendingCreateObjects);
                if (buffer) {
                    for (const peer of this.peers.values()) {
                        peer.sendReliableBuffer(buffer);
                    }
                    // this.broadcastReliableBuffer(buffer);
                }
                this.pendingCreateObjects = [];
            }
            if (this.pendingRPCs.length) {
                const buffer = NetworkMsgPacker.packRPCsInArrayBuffer(this.pendingRPCs);
                if (buffer) {
                    this.broadcastReliableBuffer(buffer);
                }
                this.pendingRPCs = [];
            }
            if (!this.hasPeers())
                return;
            const time = Date.now();
            if (time - this.lastSendUpdate >= this.sendUpdateTime) {
                this.lastSendUpdate = time;
                this.updateSyncObjects();
            }
            if (time - this.lastSendTimeSync >= this.sendTimeSyncTime) {
                this.lastSendTimeSync = time;
                const buffer = new ArrayBuffer(Uint8Array.BYTES_PER_ELEMENT + Float64Array.BYTES_PER_ELEMENT);
                const stream = new ByteStreamWriter(buffer);
                stream.uint8(BufferMessageType.TymeSync);
                stream.float64(Date.now());
                this.broadcastUnreliableBuffer(buffer);
            }
        };
        this.onLateUpdate = () => {
            this.lateUpdateDelegate.invoke();
            try {
                for (const peer of this.peers.values()) {
                    peer.flushMessages();
                }
            }
            catch (error) {
                console.error("[multiplayer]", error);
            }
        };
        this.messageHandler = new PeerMessageHandler(this);
        connection.subscribeForAskRequest(RequestAvatarModelUIDMessage, (_msg, askId) => {
            connection.send(new ResponseAvatarModelUIDMessage(this.myAvatarUid), askId);
        });
        connection.subscribeByType(ChangeLocalUserInfoMessage, this.handleChangeLocalUserInfoMessage);
        connection.subscribe([BroadcastToPeerMessage.typestatic, PeersSelectionVisibilityMessage.typestatic, RequestAvatarModelUIDMessage.typestatic], (msg) => {
            if (BroadcastToPeerMessage.is(msg)) {
                this.broadcastBusMessagesCache(msg.msgs);
            }
            else if (PeersSelectionVisibilityMessage.is(msg)) {
                this.changeRemoteSelectionVisibility(msg);
            }
        });
        window.addEventListener("beforeunload", () => {
            this.closeAllPeers();
        });
    }
    changeRemoteSelectionVisibility(msg) {
        this.peerSelectionVisible = msg.visible;
        for (const c of this.peersControllers.values()) {
            if (msg.controllerUid !== undefined && c.controllerUid !== msg.controllerUid)
                continue;
            c.changeSelectionVisibility(this.peerSelectionVisible);
        }
    }
    init() {
        animationFrame$.add(this.onFrame);
        animationFrame$.onLateUpdate$.add(this.onLateUpdate);
    }
    getRoomDepthDiffToPeer(modelUID) {
        const currentRoom = modelPool.getModelOrThrow(this.currentRoomUID);
        const model = modelPool.getModelOrThrow(modelUID);
        return currentRoom.getRoomDepth() - model.getRoomDepth();
    }
    isPeerAudibleForCurrentRoom(modelUID) {
        const currentRoom = modelPool.getModelOrThrow(this.currentRoomUID);
        const peerRoom = modelPool.getModelOrThrow(modelUID);
        if (currentRoom.equals(peerRoom))
            return true;
        const peerParentRoom = peerRoom.getRoom();
        if ((peerParentRoom === null || peerParentRoom === void 0 ? void 0 : peerParentRoom.equals(currentRoom)) === true)
            return true;
        const currentParentRoom = currentRoom.getRoom();
        if ((currentParentRoom === null || currentParentRoom === void 0 ? void 0 : currentParentRoom.equals(peerRoom)) === true)
            return true;
        return false;
    }
    isModelVisibleForPeer(modelUID, remoteView = true) {
        void remoteView;
        return this.currentRoomUID === modelUID;
    }
    onRoomChanged(newRoomUID) {
        const newRoom = modelPool.getModelOrThrow(newRoomUID);
        if (this.currentRoomUID === newRoom.getUID())
            return;
        this.currentRoomUID = newRoom.getUID();
        {
            const path = modelToAbsolutePath(newRoom);
            const buffer = new ArrayBuffer(Uint8Array.BYTES_PER_ELEMENT + calcStringByteLength(path));
            const stream = new ByteStreamWriter(buffer);
            stream.uint8(BufferMessageType.RoomChanged);
            stream.utf16String(path);
            this.broadcastReliableBuffer(buffer);
        }
        serverConnection.sendArrayBuffer(createChangeRoomBuffer(newRoom.getUID()));
        if (!newRoom.isRoot()) {
            const buffer = NetworkMsgPacker.packCreateObjectsArrayBuffer([...this.syncObjectsToSend.values()]);
            if (buffer) {
                this.broadcastReliableBuffer(buffer);
                this.pendingCreateObjects = [];
            }
        }
        for (const avatar of this.peerAvatars.values()) {
            avatar.onClientRoomChanged();
        }
        for (const controller of this.localControllers.values()) {
            controller.currentRoomUID = newRoom.getUID();
        }
        for (const controller of this.peersControllers.values()) {
            controller.onClientRoomChanged(newRoom.getUID());
        }
    }
    createPeer(clientID, remoteClientID, remoteClientIdx, initiator) {
        const peer = this.peers.get(remoteClientID);
        if (peer) {
            return Promise.resolve(peer);
        }
        const pendingCreation = this.pendingPeerCreations.get(remoteClientID);
        if (pendingCreation) {
            return pendingCreation;
        }
        const creation = this.createPeerConnection(clientID, remoteClientID, remoteClientIdx, initiator);
        this.pendingPeerCreations.set(remoteClientID, creation);
        const clearPendingCreation = () => {
            if (this.pendingPeerCreations.get(remoteClientID) === creation) {
                this.pendingPeerCreations.delete(remoteClientID);
            }
        };
        void creation.then(clearPendingCreation, clearPendingCreation);
        return creation;
    }
    createPeerConnection(clientID, remoteClientID, remoteClientIdx, initiator) {
        return __awaiter(this, void 0, void 0, function* () {
            const clientInfo = { clientID, remoteClientID, remoteClientIdx };
            console.debug(`[multiplayer] Creating peer`, clientInfo);
            const peer = new PeerConnection(clientID, remoteClientID, remoteClientIdx, initiator);
            const pendingPeerEvents = [];
            let isPeerRegistered = false;
            let channelsOpened = false;
            let channelOpenHandled = false;
            const handleChannelOpen = () => {
                if (!channelsOpened || !isPeerRegistered || channelOpenHandled)
                    return;
                channelOpenHandled = true;
                this.onChannelOpen(remoteClientID);
            };
            peer.webRTC.registerOnChannelsOpened(() => {
                channelsOpened = true;
                handleChannelOpen();
            });
            peer.webRTC.registerOnMessage((data) => {
                if (!isPeerRegistered) {
                    pendingPeerEvents.push({ type: "message", data });
                    return;
                }
                this.messageHandler.onPeerData(remoteClientID, data);
            });
            peer.webRTC.registerOnBuffer((buffer) => {
                if (!isPeerRegistered) {
                    pendingPeerEvents.push({ type: "buffer", buffer });
                    return;
                }
                this.messageHandler.onPeerBuffer(remoteClientID, buffer);
            });
            this.connection.send(new PeerCreatedMessage(remoteClientID, remoteClientIdx));
            peer.webRTC.destroyMeDelegate.set((webRTC) => {
                this.removePeer(webRTC.remoteClientId);
            });
            try {
                yield peer.webRTC.create();
            }
            catch (error) {
                console.error("[multiplayer] Error setting up peer connection", error);
            }
            this.addPeer(remoteClientID, peer);
            isPeerRegistered = true;
            for (const event of pendingPeerEvents) {
                if (event.type === "message") {
                    this.messageHandler.onPeerData(remoteClientID, event.data);
                }
                else {
                    this.messageHandler.onPeerBuffer(remoteClientID, event.buffer);
                }
            }
            pendingPeerEvents.length = 0;
            handleChannelOpen();
            void peer.mediaConnections.join().catch((error) => {
                console.error(`[multiplayer] Error setting up media connections for peer ${remoteClientID}`, error);
            });
            if (peer.webRTC.getConnectionState() === "connected") {
                this.connection.send(new PeerAddedMessage(remoteClientID, remoteClientIdx));
            }
            else {
                peer.webRTC.connectedDelegate.set(() => {
                    this.connection.send(new PeerAddedMessage(remoteClientID, remoteClientIdx));
                });
            }
            return peer;
        });
    }
    addPeer(clientID, peer) {
        this.peers.set(clientID, peer);
    }
    removePeer(clientId, force = false) {
        this.removeAllObjectsWithOwnerId(clientId);
        const client = this.peers.get(clientId);
        if (client) {
            historyEventService.addLocalHistoryEvent({
                type: HistoryEventType.userLeft,
                value: usersService.getDisplayName(clientId),
                clientId: clientId
            });
            if (force) {
                client.webRTC.onServerDisconnect();
                void client.mediaConnections.close();
            }
            client.onDestroy();
            this.peers.delete(clientId);
            this.connection.send(new PeerRemovedMessage(clientId));
        }
        usersService.removeUser(clientId);
    }
    removeAllPeers() {
        for (const clientId of this.peers.keys()) {
            this.removePeer(clientId, true);
        }
    }
    hasPeers() {
        return this.peers.size > 0;
    }
    getPeer(clientId) {
        return this.peers.get(clientId);
    }
    getSyncObjectByUid(uid) {
        return this.syncObjectUids.get(uid);
    }
    setMyAvatar() {
        const localClientIdx = usersService.getLocalUser().clientIdx;
        const avatarModelIds = generateAvatarModelIds(localClientIdx);
        this.myAvatarRootUid = avatarModelIds.rootUid;
        this.myAvatarUid = avatarModelIds.headUid;
        usersService.setUserAvatarUid(usersService.getLocalUser().clientId, this.myAvatarUid);
        ensureLocalPlayerRootModel();
        ensureLocalPlayerHeadModel();
        const avatarRootModel = modelPool.getModelOrThrow(this.myAvatarRootUid);
        const avatarHeadModel = modelPool.getModelOrThrow(this.myAvatarUid);
        const rootCreationMessage = new CreateSubModelMessage(this.myAvatarRootUid);
        rootCreationMessage.path = modelToAbsolutePath(avatarRootModel);
        const headCreationMessage = new CreateSubModelMessage(this.myAvatarUid);
        headCreationMessage.path = modelToAbsolutePath(avatarHeadModel);
        const deleteOnDisconnectMessage = new UpdateFacetOnModelMessage(this.myAvatarRootUid, new DeleteModelOnDisconnectFacet(localClientIdx));
        this.connection.sendMultiple([
            rootCreationMessage,
            headCreationMessage,
            deleteOnDisconnectMessage
        ]);
        // Player models live under [players], which is filtered from normal session-model sync.
        // Send the canonical player root + disconnect facet explicitly to the server.
        serverConnection.queueBusMessage(rootCreationMessage);
        serverConnection.queueBusMessage(deleteOnDisconnectMessage);
        // this.connection.send(new UpdateFacetOnModelMessage(this.myAvatarUid, new MediaScreenFacet())) // TODO: Do we need this here?
    }
    getClientIdxOrThrow(clientId) {
        const user = usersService.getUserByClientId(clientId);
        if (user !== undefined && user.clientIdx >= 0) {
            return user.clientIdx;
        }
        if (clientId === usersService.getLocalUser().clientId) {
            return usersService.getLocalUser().clientIdx;
        }
        const peer = this.getPeer(clientId);
        if (peer !== undefined) {
            return peer.remoteClientIdx;
        }
        throw new Error(`[multiplayer] No clientIdx found for client ${clientId}`);
    }
    addAvatar(clientId, avatar) {
        this.peerAvatars.set(clientId, avatar);
    }
    removeAvatar(clientId) {
        return this.peerAvatars.delete(clientId);
    }
    addLocalController(uid, type, hand, rayOrigin, rayDirection, hasTrackedHand = false) {
        this.removeLocalController(uid);
        const userInfo = usersService.getLocalUser();
        if (!userInfo) {
            throw new Error("UserInfo for local user is undefined");
        }
        const controller = this.createMyNetObject(ControllerPeer);
        controller.controllerUid = uid;
        controller.type = type;
        controller.currentRoomUID = this.currentRoomUID;
        controller.hand = hand;
        controller.baseColor = userInfo.avatar.baseColor;
        controller.hasTrackedHand = hasTrackedHand;
        controller.selectionRayOrigin = rayOrigin;
        controller.selectionRayDirection = rayDirection;
        this.localControllers.set(uid, controller);
        this.registerMyNetworkObject(controller);
    }
    updateLocalControllerTracking(uid, hasTrackedHand) {
        const controller = this.localControllers.get(uid);
        if (!controller)
            return;
        controller.hasTrackedHand = hasTrackedHand;
    }
    removeLocalController(uid) {
        const object = this.localControllers.get(uid);
        if (!object)
            return;
        this.localControllers.delete(uid);
        this.unregisterMyNetworkObject(object.netUid);
        if (object.destroyFromClient) {
            object.destroyFromClient();
        }
    }
    onMoveController(message) {
        var _a, _b, _c, _d;
        const object = this.localControllers.get(message.uid);
        if (!object)
            return;
        object.onControllerMoved((_b = (_a = message.visualTransform) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : message.position, (_d = (_c = message.visualTransform) === null || _c === void 0 ? void 0 : _c.orientation) !== null && _d !== void 0 ? _d : message.orientation, message.scale);
    }
    *getPeerControllerIterator(roomUid) {
        for (const controller of this.peersControllers.values()) {
            if (controller.controllerUid === undefined)
                continue;
            if (controller.currentRoomUID === undefined)
                continue;
            if (controller.currentRoomUID !== roomUid)
                continue;
            yield controller;
        }
    }
    removeAllObjectsWithOwnerId(clientId) {
        for (const [netUid, obj] of this.syncObjectUids) {
            if (obj.ownerId === clientId) {
                this.removeRemoteObject(netUid);
            }
        }
        const models = this.destroyModelsOnDisconnect.get(clientId);
        if (models) {
            for (const uid of models) {
                this.pendingBusMessages.push(new RemoveModelMessage(uid));
            }
        }
    }
    broadcastReliable(data) {
        for (const peer of this.peers.values()) {
            peer.sendReliable(data);
        }
    }
    broadcastReliableBuffer(buffer) {
        for (const peer of this.peers.values()) {
            peer.sendReliableBuffer(buffer);
        }
    }
    broadcastUnreliableBuffer(buffer) {
        for (const peer of this.peers.values()) {
            peer.sendUnreliableBuffer(buffer);
        }
    }
    broadcastBusMessagesCache(messages) {
        for (const msg of messages) {
            for (const peer of this.peers.values()) {
                peer.sendReliableBusMessageCached(msg);
            }
        }
    }
    broadcastBusMessage(msg) {
        for (const peer of this.peers.values()) {
            if (peer.isInVisibleRoom(this.currentRoomUID)) {
                peer.sendReliableBusMessageCached(msg);
            }
        }
    }
    sendToReliableBuffer(clientId, buffer) {
        const client = this.peers.get(clientId);
        if (!client)
            return;
        client.sendReliableBuffer(buffer);
    }
    sendToBusMessage(clientId, msg) {
        const client = this.peers.get(clientId);
        if (!client)
            return false;
        client.sendReliableBusMessageCached(msg);
        return true;
    }
    createMyNetObject(type) {
        const netUid = hashCode(generateUUID());
        return NetworkObjectFactory.create(this, type.staticObjectType, usersService.getLocalUser().clientId, netUid);
    }
    registerMyNetworkObject(object) {
        this.syncObjectsToSend.set(object.netUid, object);
        this.lastSentSyncObjectStates.delete(object.netUid);
        if (this.hasPeers() && object.constructorSerialization !== undefined) {
            this.pendingCreateObjects.push(object);
        }
    }
    unregisterMyNetworkObject(netUid) {
        const object = this.syncObjectsToSend.get(netUid);
        if (object === undefined)
            return;
        this.syncObjectsToSend.delete(netUid);
        this.lastSentSyncObjectStates.delete(netUid);
        if (this.hasPeers()) {
            this.pendingRemoveObjects.push(object);
            const index = this.pendingCreateObjects.indexOf(object);
            if (index !== -1) {
                this.pendingCreateObjects.splice(index, 1);
            }
        }
    }
    registerNetworkObject(netUid, object) {
        this.syncObjectUids.set(netUid, object);
    }
    unregisterNetworkObject(netUid) {
        this.syncObjectUids.delete(netUid);
    }
    removeRemoteObject(netUid) {
        const object = this.getSyncObjectByUid(netUid);
        if (!object)
            return;
        this.unregisterNetworkObject(netUid);
        if (object.onRemoteRemove) {
            object.onRemoteRemove();
        }
    }
    addDestroyModelOnDisconnect(clientId, uid) {
        let models = this.destroyModelsOnDisconnect.get(clientId);
        if (!models) {
            this.destroyModelsOnDisconnect.set(clientId, (models = new Set()));
        }
        models.add(uid);
    }
    removeDestroyModelsOnDisconnectByUid(uid) {
        for (const models of this.destroyModelsOnDisconnect.values()) {
            models.delete(uid);
        }
    }
    rpc(data) {
        if (!this.syncObjectsToSend.has(data.netUid)) {
            throw new Error(`rpc: You are not owner for netObject with netUid ${data.netUid}`);
        }
        this.pendingRPCs.push(data);
    }
    onRPC(netUid, rpcId, params) {
        const obj = this.getSyncObjectByUid(netUid);
        if (obj === undefined) {
            console.error(`[multiplayer] OnRPC: NetObject with id ${netUid} not found`);
            return;
        }
        const rpcInfo = PeerManager.rpcRegistry.get(rpcId);
        if (rpcInfo === undefined) {
            console.log(`[multiplayer] OnRPC: No rpc registered for id ${rpcId}`);
            return;
        }
        const func = obj[rpcInfo.name];
        if (func === undefined) {
            console.log(`[multiplayer] OnRPC: Net Object ${netUid} has no function with name ${rpcInfo.name}`);
            return;
        }
        if (params) {
            // valid params
            const paramLength = params.length;
            if (paramLength !== rpcInfo.paramsTypes.length) {
                console.log(`[multiplayer] OnRPC: Parameter type ${rpcInfo.paramsTypes.length} was expected, but ${paramLength} was received`);
                return;
            }
            for (let i = 0; i < paramLength; ++i) {
                const expectedType = rpcInfo.paramsTypes[i];
                const actualType = typeof params[i];
                const validType = (expectedType === "string" && actualType === "string")
                    || (expectedType === "number" && actualType === "number");
                if (!validType) {
                    console.log(`[multiplayer] OnRPC: Parameter type ${rpcInfo.paramsTypes[i]} was expected, but ${typeof params[i]} was received`);
                    return;
                }
            }
            func(...params);
        }
        else {
            func();
        }
    }
    onChannelOpen(clientId) {
        const ownUser = usersService.getLocalUser();
        if (ownUser.clientId === UNDEFINED_CLIENT_ID) {
            throw new Error("No local user set");
        }
        this.broadcastReliable({
            channel: "userinfo",
            payload: {
                identity: ownUser.identity,
                avatar: ownUser.avatar,
                customUsername: usersService.getCustomUsername(),
                guest: ownUser.guest
            }
        });
        const currentRoom = modelPool.getModelOrThrow(this.currentRoomUID);
        if (!currentRoom.isRoot()) {
            if (this.pendingCreateObjects.length) {
                const pendingBuffer = NetworkMsgPacker.packCreateObjectsArrayBuffer(this.pendingCreateObjects);
                if (pendingBuffer) {
                    for (const [peerId, peer] of this.peers) {
                        if (peerId === clientId)
                            continue;
                        peer.sendReliableBuffer(pendingBuffer);
                    }
                    this.pendingCreateObjects = [];
                }
            }
            const buffer = NetworkMsgPacker.packCreateObjectsArrayBuffer([...this.syncObjectsToSend.values()]);
            if (!buffer)
                return;
            this.sendToReliableBuffer(clientId, buffer);
        }
        else {
            console.log("[multiplayer] Channel opened, but user is in root room. NetObject will be created later");
        }
    }
    updateSyncObjects() {
        let hasVisiblePeer = false;
        for (const peer of this.peers.values()) {
            if (this.isModelVisibleForPeer(peer.getCurrentRoomModelUID(), false)) {
                hasVisiblePeer = true;
                break;
            }
        }
        if (!hasVisiblePeer)
            return;
        const changedStates = [];
        for (const obj of this.syncObjectsToSend.values()) {
            const bL = obj.byteLength;
            if (obj.serialization === undefined || bL === undefined)
                continue;
            const stateBuffer = new ArrayBuffer(UID_BYTES_PER_ELEMENT + bL);
            const stateStream = new ByteStreamWriter(stateBuffer);
            stateStream.uid(obj.netUid);
            obj.serialization(stateStream);
            const state = new Uint8Array(stateBuffer);
            const lastState = this.lastSentSyncObjectStates.get(obj.netUid);
            if (lastState !== undefined && this.haveSameBytes(lastState, state))
                continue;
            this.lastSentSyncObjectStates.set(obj.netUid, state);
            changedStates.push(state);
        }
        if (changedStates.length === 0)
            return;
        const byteLength = Uint8Array.BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT +
            changedStates.reduce((length, state) => length + state.byteLength, 0);
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(BufferMessageType.SyncData);
        stream.uint32(changedStates.length);
        const target = new Uint8Array(buffer);
        let offset = stream.getUsedByteLength();
        for (const state of changedStates) {
            target.set(state, offset);
            offset += state.byteLength;
        }
        for (const peer of this.peers.values()) {
            if (!this.isModelVisibleForPeer(peer.getCurrentRoomModelUID(), false))
                continue;
            peer.sendUnreliableBuffer(buffer);
        }
    }
    haveSameBytes(left, right) {
        if (left.byteLength !== right.byteLength)
            return false;
        for (let i = 0; i < left.byteLength; ++i) {
            if (left[i] !== right[i])
                return false;
        }
        return true;
    }
    closeAllPeers() {
        for (const peer of this.peers.values()) {
            peer.close();
        }
        this.peers.clear();
    }
}
PeerManager.rpcRegistry = new Map();
